<template>
    <Head :title="auto_accept ? 'Opening shared profile' : 'Accept profile invitation'" />
    <BaseLayout gap="lg" fill-height content-class="justify-center text-ink">
        <Transition mode="out-in" name="fade">
            <LoadingStep v-if="isAccepting" :main-loading-message="`Opening ${formattedName}'s profile...`" :hide-messages="true" />
            <BaseNotification v-else-if="auto_accept && error" theme="error" :text="error" hide-dismiss />
            <div v-else class="flex flex-col gap-6 rounded-2xl bg-surface-raised border border-border shadow-sm p-6 md:p-10">
                <BasePageTitle :title="title">
                    <template #subtitle>
                        Accepting will let you view their Collaboration Profile<template v-if="canComparePerspectives"
                            >, including their Perspective results when available,</template
                        >
                        and add them to My People, so Nadia can provide personalized coaching on working with them.
                        <template v-if="canComparePerspectives">When you both have Perspective results, you can compare your working styles.</template>
                    </template>
                </BasePageTitle>
                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />
                <BaseButton theme="primary" class="self-center" @click="handleAccept">Accept and view</BaseButton>
            </div>
        </Transition>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import { capitalizeName } from "~vue/utils/stringUtils";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { ProfileShareAttribution, ShareInvitationAccepted, ShareInvitationAcceptRequest } from "~vue/api/generated/collab-profiles";
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import LoadingStep from "~vue/components/ProfileWizard/LoadingStep.vue";
import { computed, onMounted, ref } from "vue";

const { canComparePerspectives } = useProfileSharingCopy();

const props = defineProps<{
    invitation_id: string;
    owner_name: string;
    target_user_uuid: string;
    profile_url: string;
    share_attribution?: ProfileShareAttribution | null;
    auto_accept?: boolean;
}>();

const isAccepting = ref<boolean>(!!props.auto_accept);
const error = ref<string | null>(null);

const formattedName = computed(() => (props.owner_name ? capitalizeName(props.owner_name) : ""));

const title = computed(() => (formattedName.value ? `${formattedName.value} shared their profile with you` : "Someone shared their profile with you"));

const acceptPayload = computed<ShareInvitationAcceptRequest>(() => ({ share_attribution: props.share_attribution ?? null }));
const { error: acceptError, execute: executeAccept } = useFetch(`/v1/shared-profile-invitations/${props.invitation_id}/accept`, { immediate: false })
    .post(acceptPayload)
    .json<ShareInvitationAccepted>();

const handleAccept = async (): Promise<void> => {
    isAccepting.value = true;
    error.value = null;
    await executeAccept();
    if (acceptError.value) {
        error.value = "This invitation is no longer valid. Please contact the profile owner.";
        isAccepting.value = false;
        return;
    }
    // Replace history so the back button doesn't return to the confirm page
    router.visit(props.profile_url, { replace: true });
};

onMounted(() => {
    if (props.auto_accept) {
        void handleAccept();
    }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
